import { FC, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePathContext } from '@new/common/hooks/usePathContext';
import { buildLessonPath } from '@new/common/lib/buildPaths';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Progress } from '@new/common/ui/Progress/Progress';
import { useGetCourseLessonsQuery, useGetCourseQuery } from '@teachur/http-client';
import { LessonObjectives } from '../LessonObjectives/LessonObjectives';
import { Loader } from '@new/common/ui/Loader/Loader';
import { Can } from '@new/casl';

interface CourseLessonsProps {
  courseId: number;
  courseStatus?: 'notStarted' | 'inProgress' | 'completed';
  isPaused?: boolean;
  titleClassName?: string;
  disableNavigation?: boolean;
}

export const CourseLessons: FC<CourseLessonsProps> = ({
  courseId,
  courseStatus = 'inProgress',
  isPaused = false,
  titleClassName,
  disableNavigation = false,
}) => {
  const [expandedLessonId, setExpandedLessonId] = useState<number | null>(null);
  const { data, isLoading } = useGetCourseLessonsQuery(courseId);
  const { data: course } = useGetCourseQuery(courseId);
  const navigate = useNavigate();
  const { degreeId } = usePathContext();
  const isExamPassed = course?.quizCompleted || course?.courseExam?.passed;
  if (isLoading) {
    return <Loader />;
  }

  if (!data || !data.length) {
    return null;
  }

  const handleCardClick = (lessonId: number) => {
    if (disableNavigation) return;
    navigate({ to: buildLessonPath({ degreeId, courseId, lessonId }) });
  };

  const handleToggleClick = (e: React.MouseEvent, lessonId: number) => {
    setExpandedLessonId((prev) => (prev === lessonId ? null : lessonId));
  };

  return (
    <div className="flex flex-col gap-3">
      <h3
        className={`font-bold m-0! ${
          titleClassName ??
          'text-(--neutral-black-NB1000)! [font-size:var(--fs-h6)]! md:[font-size:var(--fs-h5)]!'
        }`}
      >
        Lessons
      </h3>

      <div className="flex flex-col gap-3">
        {data.map((lesson) => {
          const isExpanded = expandedLessonId === lesson.id;

          return (
            <div
              key={lesson.id}
              className="rounded-lg bg-(--neutral-white-NW900) overflow-hidden border-2! border-(--neutral-black-NB200)! shadow-xs hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center">
                <div
                  onClick={(e) => handleToggleClick(e, lesson.id)}
                  className="cursor-pointer self-stretch flex items-center justify-center pl-4 pr-3"
                >
                  {isExpanded ? (
                    <ChevronDown size={20} className="text-(--neutral-black-NB600)" />
                  ) : (
                    <ChevronRight size={20} className="text-(--neutral-black-NB600)" />
                  )}
                </div>

                <div
                  className={`flex flex-col flex-1 md:flex-row md:justify-between gap-3 md:gap-5 p-4 ${disableNavigation ? '' : 'cursor-pointer'}`}
                  onClick={disableNavigation ? undefined : () => handleCardClick(lesson.id)}
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <p className="[font-size:var(--fs-p4)]! leading-tight text-(--neutral-black-NB1000)! font-semibold m-0!">
                      {lesson.name}
                    </p>
                    {lesson.shortDescription && (
                      <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)! m-0!">
                        {lesson.shortDescription}
                      </p>
                    )}
                  </div>

                  <Can I="start" a="Course">
                    <div className="w-full md:w-fit flex items-center gap-2 shrink-0">
                      <Progress value={lesson.progress} className="w-full md:w-24 h-2!" />
                      <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)! whitespace-nowrap">
                        {lesson.progress}%
                      </span>
                    </div>
                  </Can>
                </div>
              </div>

              {isExpanded && (
                <div
                  className="p-4 flex flex-col gap-4 border-t"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LessonObjectives
                    lessonId={lesson.id}
                    courseId={courseId}
                    courseStatus={courseStatus}
                    isPaused={isPaused}
                    isExamPassed={isExamPassed}
                    isQuizCompleted={course?.quizCompleted}
                    disableNavigation={disableNavigation}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
